// ─────────────────────────────────────────────────────────────
// printConfig.js — Noble Enterprises (tape module) print engine
// Letterhead, palette, layout constants, formatters, terms.
// This file is the ONLY place branding/styling is defined.
// ─────────────────────────────────────────────────────────────

// ─── Company letterhead ──────────────────────────────────────
// TODO: replace with real Noble Enterprises details
export const COMPANY = {
  name: 'Noble Enterprises',
  tagline: 'Adhesive Tape Manufacturing',
  address: 'Faisalabad, Pakistan',
  phone: '+92 300 0000000',
  landline: '041-0000000',
  email: 'info@nobleenterprises.pk',
  website: 'www.nobleenterprises.pk',
  strn: '0000000000000',
  ntn: '0000000-0',
};

// ─── Logo ────────────────────────────────────────────────────
// dataUrl === null  → engine draws a bordered box with `initials`
// dataUrl set       → engine draws the image at the same position
export const LOGO = {
  dataUrl: null,
  format: 'PNG',
  initials: 'NE',
  width: 12,
  height: 12,
};

// ─── Palette (RGB triplets for jsPDF) ────────────────────────
export const PALETTE = {
  ink: [40, 40, 40],
  inkSoft: [60, 60, 60],
  muted: [90, 90, 90],
  faint: [150, 150, 150],
  navy: [44, 62, 80],
  heading: [26, 60, 110],

  border: [150, 150, 150],
  rule: [40, 40, 40],

  headFill: [232, 232, 232],
  footFill: [245, 245, 245],
  grandFill: [240, 240, 240],
  zebra: [250, 250, 250],

  // tone colours — ledgers, P&L sections
  credit: [21, 128, 61],
  debit: [185, 28, 28],
  income: [21, 128, 61],
  incomeFill: [235, 247, 238],
  expense: [185, 28, 28],
  expenseFill: [253, 240, 240],
  net: [26, 60, 110],
  netFill: [232, 238, 247],
};

// ─── Layout constants (mm) ───────────────────────────────────
export const LAYOUT = {
  marginX: 12,
  headerTop: 14,
  boxHeight: 34,
  boxGap: 6,
  totalsBoxWidth: 70,
  rowGap: 5.5,
  signatureWidth: 60,
  bottomSafe: 16,
};

export const FONTS = {
  title: 18,
  boxHeading: 9,
  label: 8,
  body: 8,
  small: 7.5,
  tiny: 7,
  totals: 8.5,
};

// ─── Formatters ──────────────────────────────────────────────
// Referenced by name from report configs: { key, label, fmt: 'money' }
const toNum = (n) => (isNaN(parseFloat(n)) ? 0 : parseFloat(n));

export const FORMATTERS = {
  text: (v) => (v === null || v === undefined || v === '' ? '—' : String(v)),

  int: (v) => Math.round(toNum(v)).toLocaleString('en-PK'),

  money: (v) => Math.round(toNum(v)).toLocaleString('en-PK'),

  money2: (v) =>
    toNum(v).toLocaleString('en-PK', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }),

  qty: (v) => toNum(v).toFixed(2).replace(/\.?0+$/, ''),

  kg: (v) => `${toNum(v).toFixed(2).replace(/\.?0+$/, '')} kg`,

  meters: (v) => {
    const n = toNum(v);
    return `${n.toLocaleString('en-PK', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })} m`;
  },

  pct: (v) => `${toNum(v).toFixed(2).replace(/\.?0+$/, '')}%`,

  rate: (v) =>
    toNum(v).toLocaleString('en-PK', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4,
    }),

  date: (v) => {
    if (!v) return '—';
    const d = new Date(v);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  },

  datetime: (v) => {
    if (!v) return '—';
    const d = new Date(v);
    if (isNaN(d.getTime())) return '—';
    return `${d.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    })} ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
  },
};

export const applyFmt = (value, fmt) => {
  if (typeof fmt === 'function') return fmt(value);
  const fn = FORMATTERS[fmt] || FORMATTERS.text;
  return fn(value);
};

// ─── Default terms ───────────────────────────────────────────
export const TERMS_PO = [
  '1) Credit days will start after receiving invoices.',
  '2) Send bill / invoice within 3 days after delivery.',
  '3) We reserve the right to cancel this PO at any time without intimation.',
  '4) Send 2 copies of delivery challan along with the PO copy.',
  '5) No payments, gifts or gratuities of any kind shall be made directly or indirectly to Noble Enterprises employees.',
  '6) Delivery must be as per the agreed lead time.',
  '7) Vendor is responsible for proper packing of the goods.',
  '8) Vendor shall bear the expense of loss or damage of goods until delivered to destination.',
];

export const TERMS_INVOICE = [
  '1) Goods once sold are not returnable without prior written approval.',
  '2) Claims regarding quantity or quality must be raised within 3 days of receipt.',
  '3) Payment as per agreed credit terms; overdue balances may attract a service charge.',
  '4) This invoice is computer generated and valid without signature.',
];

// ─── Default signature blocks ────────────────────────────────
export const SIGNATURES = {
  po: ['Order By', 'Approved By', 'Audit Approved By'],
  invoice: ['Prepared By', 'Received By'],
  report: ['Prepared By', 'Checked By'],
};

// ─── Footer ──────────────────────────────────────────────────
export const FOOTER = {
  left: () => `Generated ${FORMATTERS.datetime(new Date())}`,
  right: (page, total) => `Page ${page} of ${total}`,
};